import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Package } from 'lucide-react';
import { fetchScrapEntriesByLine, deleteScrapEntry } from '@/core/api';
import { useTenant } from '@/core/hooks/useTenant';
import { useLanguage } from '@/core/LanguageContext';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { useToast } from '@/shared/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/ui/alert-dialog';

interface ScrapEntryListProps {
  factoryId: string;
  entryDate: string;
  lineId: string;
}

/**
 * Compact list of scrap entries for a line.
 */
export const ScrapEntryList = ({ factoryId, entryDate, lineId }: ScrapEntryListProps) => {
  const { selectedTenantId } = useTenant();
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['scrap-entries', selectedTenantId, factoryId, entryDate, lineId],
    queryFn: () => fetchScrapEntriesByLine(factoryId, entryDate, lineId),
    enabled: !!factoryId && !!lineId,
  });

  const deleteMutation = useMutation({
    mutationFn: (entryId: string) => deleteScrapEntry(entryId),
    onSuccess: () => {
      toast({ title: t('core.scrap.entryDeleted', 'Entry deleted') });
      queryClient.invalidateQueries({ queryKey: ['scrap-entries'] });
      queryClient.invalidateQueries({ queryKey: ['scrap-entries-area'] });
      queryClient.invalidateQueries({ queryKey: ['scrap-daily-summaries'] });
    },
    onError: (error: Error) => {
      toast({ title: t('core.scrap.error', 'Error'), description: error.message, variant: 'destructive' });
    },
  });

  if (isLoading) {
    return <div className="h-8 bg-muted/50 animate-pulse rounded" />;
  }

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      {entries.map((entry) => (
        <div
          key={entry.id}
          className="flex items-center gap-2 py-1.5 px-2 rounded bg-muted/30 hover:bg-muted/50 transition-colors group"
        >
          <span className="font-medium text-sm min-w-[70px]">
            €{entry.loss_amount.toFixed(2)}
          </span>

          {entry.article && (
            <Badge variant="secondary" className="gap-1 h-5 text-xs">
              <Package className="h-3 w-3" />
              {entry.article.code}
            </Badge>
          )}

          {entry.station && (
            <Badge variant="outline" className="h-5 text-xs">
              {entry.station.name}
            </Badge>
          )}

          {entry.comment && (
            <span className="text-xs text-muted-foreground truncate flex-1" title={entry.comment}>
              {entry.comment}
            </span>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('core.scrap.deleteEntry', 'Delete entry?')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('core.scrap.deleteConfirm', 'Remove €{amount} scrap entry? This cannot be undone.').replace('{amount}', entry.loss_amount.toFixed(2))}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('core.scrap.cancel', 'Cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteMutation.mutate(entry.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {t('core.scrap.delete', 'Delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ))}
    </div>
  );
};
